import { createContext, createElement, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { apiDelete, apiGet } from 'src/lib/api/axios';
import { resolveTenantCode } from 'src/lib/tenantRouting';
import { Chat } from '../types';
import { usePostHog } from 'src/lib/posthog';
import useAuth from 'src/hooks/useAuth';

interface UseChatListReturn {
  chatList: Chat[];
  chatListError: string;
  chatListLoading: boolean;
  loadMoreLoading: boolean;
  searchLoading: boolean;
  fetchChatList: (currentLimit: number) => Promise<void>;
  handleLoadMore: () => void;
  debouncedSearch: (value: string) => void;
  search: string;
  setSearch: (value: string) => void;
  moreRemoteChats: boolean;
  deleteChat: (id: string) => Promise<boolean>;
  addNewChat: (chat: Chat) => void;
  updateChatTopic: (id: string, topic: string, moveToTop?: boolean) => void;
  markChatHasCanvas: (id: string) => void;
}

const ChatListContext = createContext<UseChatListReturn | null>(null);

const useChatList = (initialLimit?: number): UseChatListReturn => {
  const [chatList, setChatList] = useState<Chat[]>([]);
  const [chatListError, setChatListError] = useState('');
  const [chatListLoading, setChatListLoading] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [limit, setLimit] = useState(initialLimit || 30);
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState('');

  const posthog = usePostHog();
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Calculate if there are more chats available
  const moreRemoteChats = chatList.length >= limit && chatList.length > 0;

  const tenantCode = resolveTenantCode(pathname, user?.tenants);

  const fetchChatList = async (currentLimit: number) => {
    if (!tenantCode) {
      setChatListLoading(false);
      return;
    }
    try {
      setLoadMoreLoading(true);
      setChatListLoading(true);
      const response = await apiGet('/chat/chats/', {
        params: {
          limit: currentLimit,
          search_term: searchTerm || undefined
        },
        meta: {
          globalError: false,
          context: 'chat-history-fetch'
        }
      });
      setChatList(response.data);
    } catch (error) {
      setChatListError('Error fetching chat list');
    } finally {
      setChatListLoading(false);
      setLoadMoreLoading(false);
    }
  };

  const deleteChat = async (id: string): Promise<boolean> => {
    try {
      await apiDelete(`chat/chats/${id}`, {
        meta: {
          globalError: false,
          context: 'chat-deletion'
        }
      });

      // Success, so remove the chat from the local copy of the chat list on the frontend
      setChatList((prevChatList) => prevChatList.filter((chat) => chat.id !== id));
      return true;
    } catch {
      setChatListError('Error deleting chat');
      return false;
    }
  };

  const addNewChat = (chat: Chat) => {
    // Add the new chat to the beginning of the list
    setChatList((prevChatList) => [chat, ...prevChatList]);
  };

  const markChatHasCanvas = useCallback((id: string) => {
    setChatList((prev) => prev.map((chat) => (chat.id === id ? { ...chat, has_canvas: true } : chat)));
  }, []);

  const updateChatTopic = (id: string, topic: string, moveToTop = false) => {
    setChatList((prevChatList) => {
      const updated = prevChatList.find((chat) => chat.id === id);
      if (!updated) return prevChatList;
      const updatedChat = moveToTop
        ? { ...updated, topic, timestamp: new Date().toISOString() }
        : { ...updated, topic };
      if (!moveToTop || prevChatList[0]?.id === id) {
        return prevChatList.map((chat) => (chat.id === id ? updatedChat : chat));
      }
      return [updatedChat, ...prevChatList.filter((chat) => chat.id !== id)];
    });
  };

  useEffect(() => {
    fetchChatList(limit);
    // Refetch when the active tenant changes so a tenant switch never
    // leaves stale chats from the previous tenant in the sidebar. Today
    // tenant switches do a full page reload (so this effect always runs
    // with the right tenantCode on first paint), but keeping tenantCode
    // in the deps is defensive against future in-app tenant switching.
  }, [limit, searchTerm, tenantCode]);

  const handleLoadMore = () => {
    setLoadMoreLoading(true);
    const chatHistoryContainer = document.querySelector('.chat-history');
    if (chatHistoryContainer) {
      // Store scroll position for restoration
      const scrollPosition = chatHistoryContainer.scrollTop;
      setTimeout(() => {
        chatHistoryContainer.scrollTop = scrollPosition;
      }, 0);
    }
    setLimit((prevLimit) => prevLimit + 50);
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (value: string) => {
        posthog?.capture('chat_list_search', { search_term: value });
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setSearchLoading(true);
          setSearchTerm(value);
        }, 500);
      };
    })(),
    []
  );

  return {
    chatList,
    chatListError,
    chatListLoading,
    loadMoreLoading,
    searchLoading,
    fetchChatList,
    handleLoadMore,
    debouncedSearch,
    search,
    setSearch,
    moreRemoteChats,
    deleteChat,
    addNewChat,
    updateChatTopic,
    markChatHasCanvas
  };
};

export const ChatListProvider = ({ children }: { children: ReactNode }) => {
  const chatList = useChatList(50);

  return createElement(ChatListContext.Provider, { value: chatList }, children);
};

export const useSharedChatList = () => {
  const chatList = useContext(ChatListContext);
  if (!chatList) {
    throw new Error('useSharedChatList must be used within a ChatListProvider');
  }

  return chatList;
};
